import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['search', 'list', 'metadata', 'loading']
  static values = {
    url: String,
    currentValue: String,
    hasSubtasks: Boolean
  }

  connect() {
    this.page = 1
    this.hasMore = true
    this.isLoading = false
    this.searchTerm = ''
    this.hasLoadedOnce = false

    // Get metadata for form building
    if (this.hasMetadataTarget) {
      this.formAction = this.metadataTarget.dataset.formAction
      this.authenticityToken = this.metadataTarget.dataset.authenticityToken
      this.turboFrame = this.metadataTarget.dataset.turboFrame
      this.field = this.metadataTarget.dataset.field
      this.paramKey = this.metadataTarget.dataset.paramKey
    }

    // Only load accounts when dropdown is opened
    // Support both Bootstrap dropdown and turbo-dropdown
    this.element.addEventListener('shown.bs.dropdown', () => {
      this.#handleDropdownOpen()
    })

    // Also support turbo-dropdown controller - listen for click on button target
    const turboDropdownButton = this.element.querySelector('[data-turbo-dropdown-target="button"]')
    if (turboDropdownButton) {
      turboDropdownButton.addEventListener('click', () => {
        // Small delay to ensure dialog is open
        setTimeout(() => this.#handleDropdownOpen(), 50)
      })
    }
  }

  #handleDropdownOpen() {
    if (!this.hasLoadedOnce) {
      this.loadAccounts()
      this.hasLoadedOnce = true
    }
    // Focus search input when dropdown opens
    if (this.hasSearchTarget) {
      this.searchTarget.focus()
    }
  }

  filter(event) {
    this.searchTerm = event.target.value.trim()
    this.page = 1
    this.hasMore = true
    this.loadAccounts(true)
  }

  preventClose(event) {
    event.stopPropagation()
  }

  scroll(event) {
    const element = event.target
    const isNearBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50

    if (isNearBottom && this.hasMore && !this.isLoading) {
      this.page++
      this.loadAccounts()
    }
  }

  async loadAccounts(isSearch = false) {
    if (this.isLoading) return

    this.isLoading = true
    if (!isSearch) {
      this.showLoading()
    }

    try {
      const baseUrl = this.urlValue.startsWith('http') ? this.urlValue : new URL(this.urlValue, window.location.origin).toString()
      const url = new URL(baseUrl)
      url.searchParams.set('paginate', 'true')
      url.searchParams.set('page', this.page)
      if (this.currentValueValue) {
        url.searchParams.set('current_value', this.currentValueValue)
      }
      if (this.searchTerm) {
        url.searchParams.set('q', this.searchTerm)
      }

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      this.hasMore = data.pagination?.more || false
      if (isSearch || this.page === 1) {
        this.clearList()
      }
      this.renderAccounts(data.results)
    } catch (error) {
      console.error('Error loading accounts:', error)
    } finally {
      this.isLoading = false
      this.hideLoading()
    }
  }

  renderAccounts(accounts) {
    accounts.forEach(account => {
      const li = document.createElement('li')
      li.className = 'list-group-item list-group-item-action p-0'
      li.dataset.searchable = account.text.toLowerCase()

      const isActive = account.is_selected || account.id.toString() === this.currentValueValue?.toString()
      const valueToSubmit = isActive ? '' : account.id

      li.innerHTML = `
        <form action="${this.formAction}" accept-charset="UTF-8" method="post" data-turbo="true" data-turbo-frame="${this.turboFrame}" class="d-block" data-account-name="${account.text}">
          <input type="hidden" name="_method" value="patch" autocomplete="off">
          <input type="hidden" name="authenticity_token" value="${this.authenticityToken}" autocomplete="off">
          <input type="hidden" name="${this.paramKey}[${this.field}]" value="${valueToSubmit}" autocomplete="off">
          <button type="submit" class="btn btn-link w-100 text-start py-2 text-decoration-none" style="color: inherit; border: none;">
            <div class="d-flex align-items-center justify-content-between">
              <span class="text-dark">${account.text}</span>
              ${isActive ? '<i class="fas fa-check-circle text-primary"></i>' : ''}
            </div>
          </button>
        </form>
      `

      // Add confirmation for account changes when task has subtasks
      const form = li.querySelector('form')
      if (this.hasSubtasksValue) {
        form.addEventListener('submit', (event) => this.confirmAccountChange(event, isActive))
      }

      this.listTarget.appendChild(li)
    })
  }

  confirmAccountChange(event, isClearing) {
    const form = event.target
    const accountName = form.dataset.accountName

    const message = isClearing
      ? 'Clearing this account will clear the account for all subtasks (except tax tasks with different accounts). Continue?'
      : `Changing the account will update all subtasks to "${accountName}" (except tax tasks with different accounts). Continue?`

    if (!confirm(message)) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  clearList() {
    const userItems = this.listTarget.querySelectorAll('li:not([data-keep])')
    userItems.forEach(item => item.remove())
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('d-none')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('d-none')
    }
  }
}
